import { useTranslation } from "@privacy/translations";
import FormControl from "@mui/material/FormControl";
import Stack from "@mui/material/Stack";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import { ErrorMessage } from "../../dynamicForm/inlineForm/ErrorMessage";
import { updateDpiaMutationOptions } from "../../api/dpia";
import type { DpiaById } from "../../api/dpia.types";
import type { ExternalDpiaStatus } from "../../api/dpia";
import { EXTERNAL_DPIA_STATUSES } from "../../api/dpia";
import { statusColors } from "./DpiaStatusChip";
import { StatusSquare } from "../../shared/RegistrationStatusDot";

const isExternalStatus = (status: DpiaById["status"]): status is ExternalDpiaStatus =>
  EXTERNAL_DPIA_STATUSES.some(externalStatus => externalStatus === status);

export const DpiaExternalStatusOption = ({ status }: { status: ExternalDpiaStatus }) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <StatusSquare sx={{ bgcolor: statusColors[status].dot }} />
      <Typography
        component="span"
        sx={theme => ({
          mt: "1px",
          fontWeight: 600,
          fontSize: theme.typography.pxToRem(12),
          color: theme.palette.text.secondary,
        })}
      >
        {t(`DpiaStatus.${status}`)}
      </Typography>
    </Box>
  );
};

export const DpiaStatusSelectExternal = ({ dpia, isReadOnly }: { dpia: DpiaById; isReadOnly: boolean }) => {
  const updateDpiaMutation = useMutation(updateDpiaMutationOptions());

  const current: ExternalDpiaStatus = isExternalStatus(dpia.status) ? dpia.status : "draft";
  const value = updateDpiaMutation.isPending ? (updateDpiaMutation.variables.payload.status ?? current) : current;

  return (
    <Stack sx={{ alignItems: "flex-end", gap: 0.5 }}>
      <FormControl>
        <Select
          id="dpia-external-status"
          size="small"
          sx={{ minWidth: 150 }}
          value={value}
          disabled={isReadOnly || updateDpiaMutation.isPending}
          endAdornment={updateDpiaMutation.isPending ? <CircularProgress size={12} sx={{ mr: 3 }} /> : null}
          onChange={event => {
            const next = event.target.value;
            if (next === current) return;
            updateDpiaMutation.mutate({ id: dpia.id, payload: { status: next } });
          }}
        >
          {EXTERNAL_DPIA_STATUSES.map(status => (
            <MenuItem key={status} value={status}>
              <DpiaExternalStatusOption status={status} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {updateDpiaMutation.isError && <ErrorMessage />}
    </Stack>
  );
};
